import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  EDUCATION_TYPES,
  EDUCATION_COURSES,
  EDUCATION_SPECIALIZATIONS,
  EDUCATION_STATUS_OPTIONS,
  EDUCATION_LEVELS,
} from '../constants/profileOptions';

const EditEducationScreen = ({ navigation, route }) => {
  const { memberId, educationId, education, allEducations: passedEducations } = route.params;
  
  const [formData, setFormData] = useState({
    educationType: education?.educationType || '',
    course: education?.course || '',
    customCourse: education?.customCourse || '',
    specialization: education?.specialization || '',
    status: education?.status || '',
    educationalQualification: education?.educationalQualification || '',
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);
  const [allEducations, setAllEducations] = useState(passedEducations || []);

  // Only fetch if allEducations wasn't passed (shouldn't happen now)
  useEffect(() => {
    if (educationId && (!passedEducations || passedEducations.length === 0)) {
      console.log('No educations passed, fetching...');
      fetchAllEducations();
    }
  }, [educationId, memberId, passedEducations]);

  const fetchAllEducations = async () => {
    try {
      console.log('Fetching all educations for memberId:', memberId);
      const response = await api.get(`/api/EnhancedMember/${memberId}/education`);
      const data = response.data?.data || response.data;
      console.log('Fetched educations:', data);
      const educationsArray = Array.isArray(data) ? data : [];
      console.log('Setting allEducations to:', educationsArray);
      setAllEducations(educationsArray);
    } catch (error) {
      console.error('Error fetching educations:', error);
      setAllEducations([]);
    }
  };

  const handleSave = async () => {
    console.log('handleSave called');
    console.log('memberId:', memberId);
    console.log('educationId:', educationId);
    console.log('formData:', formData);
    
    // Validate required fields
    if (!formData.educationType) {
      if (Platform.OS === 'web') {
        alert('Please select Education Type');
      } else {
        Alert.alert('Required Field', 'Please select Education Type');
      }
      return;
    }

    // Check if "No Education" is selected when other qualifications exist
    if (formData.educationType === 'no_education') {
      const hasOtherEducation = allEducations.some(edu => 
        edu.educationId !== educationId && edu.educationType !== 'no_education'
      );
      
      if (hasOtherEducation) {
        const errorMessage = 'Cannot select "No Education" when other qualifications are already added.';
        if (Platform.OS === 'web') {
          alert(errorMessage);
        } else {
          Alert.alert('Validation Error', errorMessage);
        }
        return;
      }
    }

    // Check if adding other education when "No Education" already exists
    if (formData.educationType !== 'no_education') {
      const hasNoEducation = allEducations.some(edu => 
        edu.educationId !== educationId && edu.educationType === 'no_education'
      );
      
      if (hasNoEducation) {
        const errorMessage = 'Cannot add other qualifications when "No Education" is already selected. Please remove "No Education" first.';
        if (Platform.OS === 'web') {
          alert(errorMessage);
        } else {
          Alert.alert('Validation Error', errorMessage);
        }
        return;
      }
    }

    // For "No Education", course and status are not required
    if (formData.educationType !== 'no_education') {
      if (!formData.course) {
        if (Platform.OS === 'web') {
          alert('Please select Course');
        } else {
          Alert.alert('Required Field', 'Please select Course');
        }
        return;
      }

      // If course is "Other", customCourse is mandatory
      if (formData.course === 'Other' && (!formData.customCourse || formData.customCourse.trim() === '')) {
        if (Platform.OS === 'web') {
          alert('Please specify the Custom Course');
        } else {
          Alert.alert('Required Field', 'Please specify the Custom Course');
        }
        return;
      }

      if (!formData.status) {
        if (Platform.OS === 'web') {
          alert('Please select Status');
        } else {
          Alert.alert('Required Field', 'Please select Status');
        }
        return;
      }
    }

    // Check for duplicate education - compare all fields EXCEPT status
    const courseToCheck = formData.course === 'Other' ? formData.customCourse : formData.course;
    const isDuplicate = allEducations.some(edu => {
      // Skip the current education if editing
      if (educationId && edu.educationId === educationId) {
        return false;
      }
      
      // Check if fields match (educationType, course/customCourse, specialization, educationalQualification)
      // NOTE: Status is NOT considered for duplicate check
      const existingCourse = edu.course === 'Other' ? edu.customCourse : edu.course;
      const educationTypeMatch = edu.educationType === formData.educationType;
      const courseMatch = existingCourse === courseToCheck;
      const specializationMatch = (edu.specialization || '') === (formData.specialization || '');
      const qualificationMatch = (edu.educationalQualification || '') === (formData.educationalQualification || '');
      
      return educationTypeMatch && courseMatch && specializationMatch && qualificationMatch;
    });

    if (isDuplicate) {
      const duplicateMessage = `This exact education qualification already exists. Please modify at least one field.`;
      if (Platform.OS === 'web') {
        alert(duplicateMessage);
      } else {
        Alert.alert('Duplicate Education', duplicateMessage);
      }
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (educationId) {
        console.log('Editing education. Using passed educations:', allEducations);
        console.log('All educations count:', allEducations.length);
        
        // Update existing education - build updated education object
        const updatedEducation = {
          memberId: memberId,
          educationId: educationId,
          ...formData,
        };

        // Create full list with updated education
        const educationList = allEducations.map(edu => 
          edu.educationId === educationId ? updatedEducation : {
            memberId: memberId,
            educationId: edu.educationId,
            educationType: edu.educationType,
            course: edu.course,
            customCourse: edu.customCourse,
            specialization: edu.specialization,
            status: edu.status,
            educationalQualification: edu.educationalQualification,
          }
        );

        console.log('Final educationList to send:', educationList);
        console.log('educationList length:', educationList.length);
        const endpoint = `/api/EnhancedMember/${memberId}/education`;
        console.log('Sending PUT to:', endpoint);
        response = await api.put(endpoint, educationList);
        console.log('PUT response:', response.data);
      } else {
        // Create new education - use POST with single object
        const payload = {
          memberId: memberId,
          ...formData,
        };
        console.log('Creating education with POST:', payload);
        const endpoint = `/api/EnhancedMember/${memberId}/education`;
        console.log('Sending POST to:', endpoint);
        response = await api.post(endpoint, payload);
        console.log('POST response:', response.data);
      }
      
      const successMessage = educationId ? 'Education details updated successfully' : 'Education added successfully';
      
      if (Platform.OS === 'web') {
        alert(successMessage);
        navigation.goBack();
      } else {
        Alert.alert('Success', successMessage, [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]);
      }
    } catch (error) {
      console.error('Error saving education:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error message:', error.message);
      
      if (Platform.OS === 'web') {
        alert(error.message || 'Failed to save education details');
      } else {
        Alert.alert('Error', error.message || 'Failed to save education details');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    console.log('handleDelete called');
    console.log('memberId:', memberId, 'educationId:', educationId);
    
    // Use platform-specific confirmation
    if (Platform.OS === 'web') {
      // Use browser's native confirm dialog for web
      if (window.confirm('Are you sure you want to delete this education record?')) {
        console.log('Delete confirmed (web)');
        deleteEducation();
      }
    } else {
      // Use Alert.alert for native platforms
      Alert.alert(
        'Delete Education',
        'Are you sure you want to delete this education record?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: () => {
              console.log('Delete confirmed (native)');
              deleteEducation();
            },
          },
        ]
      );
    }
  };

  const deleteEducation = async () => {
    try {
      console.log('Deleting education with educationId:', educationId);
      const endpoint = `/api/EnhancedMember/education/${educationId}`;
      console.log('Sending DELETE to:', endpoint);
      const response = await api.delete(endpoint);
      console.log('DELETE response:', response.data);
      
      if (Platform.OS === 'web') {
        alert('Education record deleted successfully');
        navigation.goBack();
      } else {
        Alert.alert('Success', 'Education record deleted', [
          { text: 'OK', onPress: () => navigation.goBack() },
        ]);
      }
    } catch (error) {
      console.error('Error deleting education:', error);
      console.error('Error response:', error.response?.data);
      
      if (Platform.OS === 'web') {
        alert(error.message || 'Failed to delete education record');
      } else {
        Alert.alert('Error', error.message || 'Failed to delete education record');
      }
    }
  };

  const openDropdown = (fieldKey, options) => {
    setActiveDropdownField(fieldKey);
    setActiveDropdownOptions(options);
    setDropdownVisible(true);
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      // Clear dependent fields when education type changes
      if (activeDropdownField === 'educationType') {
        // If "No Education" is selected, auto-select course and clear status
        if (value === 'no_education') {
          setFormData(prev => ({
            ...prev,
            educationType: value,
            course: 'No Formal Education',
            specialization: '',
            status: '',
            customCourse: '',
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            educationType: value,
            course: '',
            specialization: '',
          }));
        }
      }
      // Clear specialization and customCourse when course changes
      else if (activeDropdownField === 'course') {
        setFormData(prev => ({
          ...prev,
          course: value,
          specialization: '',
          customCourse: value === 'Other' ? prev.customCourse : '', // Clear customCourse unless "Other" is selected
        }));
      }
      // For other fields, just update the value
      else {
        setFormData(prev => ({
          ...prev,
          [activeDropdownField]: value,
        }));
      }
    }
    setDropdownVisible(false);
    setActiveDropdownField(null);
  };

  const getCourseOptions = () => {
    if (!formData.educationType) return [];
    return EDUCATION_COURSES[formData.educationType] || [];
  };

  const getSpecializationOptions = () => {
    if (!formData.course) return [];
    return EDUCATION_SPECIALIZATIONS[formData.course] || [];
  };

  return (
    <View style={profileDetailStyles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <SvgIcon name="arrow-back" size={24} color={colors.white} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{educationId ? 'Edit Education' : 'Add Education'}</Text>
        <View style={styles.deleteButton} />
      </View>

      <ScrollView style={profileDetailStyles.scrollView} contentContainerStyle={profileDetailStyles.scrollContent}>
        {/* Education Type */}
        <View style={profileDetailStyles.inputWrapper}>
          <Text style={profileDetailStyles.floatingLabel}>Education Type *</Text>
          <TouchableOpacity
            style={profileDetailStyles.dropdown}
            onPress={() => openDropdown('educationType', EDUCATION_TYPES)}
          >
            <Text style={formData.educationType ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
              {EDUCATION_TYPES.find(opt => opt.value === formData.educationType)?.label || 'Select Education Type'}
            </Text>
            <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Course - Hide for "No Education" */}
        {formData.educationType && formData.educationType !== 'no_education' && (
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>Course *</Text>
            <TouchableOpacity
              style={profileDetailStyles.dropdown}
              onPress={() => openDropdown('course', getCourseOptions())}
              disabled={!formData.educationType}
            >
              <Text style={formData.course ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {getCourseOptions().find(opt => opt.value === formData.course)?.label || 'Select Course'}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Custom Course - Show only when 'Other' is selected */}
        {formData.course === 'Other' && (
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>Custom Course *</Text>
            <TextInput
              style={profileDetailStyles.input}
              value={formData.customCourse}
              onChangeText={(text) => setFormData({ ...formData, customCourse: text })}
              placeholder="Enter custom course name"
              placeholderTextColor={colors.textSecondary}
            />
          </View>
        )}

        {/* Specialization */}
        {formData.course && getSpecializationOptions().length > 0 && (
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>Specialization</Text>
            <TouchableOpacity
              style={profileDetailStyles.dropdown}
              onPress={() => openDropdown('specialization', getSpecializationOptions())}
              disabled={!formData.course}
            >
              <Text style={formData.specialization ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {getSpecializationOptions().find(opt => opt.value === formData.specialization)?.label || 'Select Specialization'}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Status - Hide for "No Education" */}
        {formData.educationType !== 'no_education' && (
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>Status *</Text>
            <TouchableOpacity
              style={profileDetailStyles.dropdown}
              onPress={() => openDropdown('status', EDUCATION_STATUS_OPTIONS)}
            >
              <Text style={formData.status ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {EDUCATION_STATUS_OPTIONS.find(opt => opt.value === formData.status)?.label || 'Select Status'}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Cancel Button */}
        <TouchableOpacity
          style={[profileDetailStyles.button, styles.cancelButton]}
          onPress={() => navigation.goBack()}
        >
          <SvgIcon name="close-circle-outline" size={20} color={colors.white} />
          <Text style={profileDetailStyles.buttonText}>Cancel</Text>
        </TouchableOpacity>

        {/* Save Button */}
        <TouchableOpacity
          style={[profileDetailStyles.button, isSaving && { opacity: 0.6 }]}
          onPress={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <ActivityIndicator color={colors.white} />
          ) : (
            <>
              <SvgIcon name="checkmark-circle-outline" size={20} color={colors.white} />
              <Text style={profileDetailStyles.buttonText}>Save Changes</Text>
            </>
          )}
        </TouchableOpacity>
      </ScrollView>

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <View style={profileDetailStyles.modalHeader}>
              <Text style={profileDetailStyles.modalTitle}>
                {activeDropdownField === 'educationType' && 'Select Education Type'}
                {activeDropdownField === 'educationalQualification' && 'Select Qualification'}
                {activeDropdownField === 'course' && 'Select Course'}
                {activeDropdownField === 'specialization' && 'Select Specialization'}
                {activeDropdownField === 'status' && 'Select Status'}
              </Text>
              <TouchableOpacity onPress={() => setDropdownVisible(false)}>
                <SvgIcon name="close" size={24} color={colors.primary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={profileDetailStyles.modalItem}
                  onPress={() => selectDropdownOption(option.value)}
                >
                  <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                  {formData[activeDropdownField] === option.value && (
                    <SvgIcon name="checkmark-circle" size={20} color={colors.secondary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: 50,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.accent,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.white,
  },
  deleteButton: {
    padding: 8,
  },
  cancelButton: {
    backgroundColor: colors.textSecondary,
    marginBottom: 12,
  },
});

export default EditEducationScreen;
